const WheelSizeEv = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/wheels-collection.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            How Wheel Size Affects Your EV’s Range & Ride Comfort
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Wheel size impacts efficiency, comfort, noise levels, and how your EV performs. Here’s how choosing the right wheel size makes a big difference.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            When it comes to electric vehicles, wheel size isn’t just a style choice — it affects battery range, comfort, noise, and overall efficiency. Larger wheels look sporty but consume more energy, while smaller wheels offer better comfort and range.
            At <strong>Express Tire Works</strong>, we help EV owners choose wheels that enhance performance without compromising efficiency. Here’s a complete guide to how wheel size impacts your EV.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. Wheel Size & EV Range: What’s the Connection?
            </h2>
            <p>
              Larger wheels are heavier and increase rolling resistance, causing your EV to use more energy. Smaller wheels are lighter, which helps conserve battery power and boost range.
            </p>

            <h3 className="mt-4 font-semibold">Larger Wheels → Lower Range</h3>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Heavier weight requires more motor effort</li>
              <li>Wider tires increase rolling resistance</li>
              <li>Reduced aerodynamic efficiency</li>
            </ul>
            <p>Switching from 18″ to 20″ wheels can reduce EV range by <strong>3–10%</strong>.</p>

            <h3 className="mt-6 font-semibold">Smaller Wheels → Higher Range</h3>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Lighter and more efficient to rotate</li>
              <li>Lower rolling resistance</li>
              <li>Improved overall energy efficiency</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Wheel Size & Ride Comfort: What Changes?
            </h2>
            <p>
              Tire sidewall height plays a major role in comfort. Larger wheels usually require low-profile tires, which reduce cushioning. Smaller wheels allow taller sidewalls for a smoother ride.
            </p>

            <h3 className="mt-4 font-semibold">Larger Wheels → Sporty but Stiffer Ride</h3>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Less shock absorption</li>
              <li>Harsher over potholes and bumps</li>
              <li>Higher risk of rim damage</li>
              <li>Sharper handling and sporty feel</li>
            </ul>

            <h3 className="mt-6 font-semibold">Smaller Wheels → Smoother Ride</h3>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Better cushioning and impact absorption</li>
              <li>More comfortable on long drives</li>
              <li>Lower chance of rim damage</li>
              <li>Longer tire life</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Wheel Noise Matters More on EVs
            </h2>
            <p>
              EV cabins are extremely quiet due to the lack of engine sound. This makes tire and road noise far more noticeable. Larger wheels with low-profile tires generally produce more noise, while smaller wheels help maintain a quieter ride.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. What Wheel Size Should You Choose for Your EV?
            </h2>

            <h3 className="font-semibold mt-4">👉 Best Range & Comfort</h3>
            <p className="mb-4">
              Choose <strong>17″–19″ wheels</strong>. Ideal for maximizing range and improving ride comfort.
            </p>

            <h3 className="font-semibold mt-4">👉 Best Performance & Style</h3>
            <p className="mb-4">
              Choose <strong>20″–22″ wheels</strong>. Stylish and sporty but reduces range.
            </p>

            <h3 className="font-semibold mt-4">👉 Balanced Choice</h3>
            <p>
              Choose <strong>19″ wheels</strong> for a great mix of comfort, efficiency, and performance.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Wheel size impacts range, comfort, noise, and driving performance — especially in electric vehicles. Before choosing new wheels, consider your daily driving needs and priorities. At <strong>Express Tire Works</strong>, we help EV owners find the perfect wheel and tire setup for maximum efficiency, safety, and style.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">EV Wheels</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Electric Vehicles</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Wheel Size</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Range Efficiency</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Ride Comfort</p>
        </div>
      </section>
    </div>
  );
};

export default WheelSizeEv;